import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { formatExecutionDuration } from "../../utils/dashboardAnalytics";

const CHART_HEIGHT = 200;
const MARGIN = { top: 12, right: 14, bottom: 34, left: 42 };
const DURATION_MARGIN = { top: 12, right: 14, bottom: 34, left: 52 };

export const AGENTS_BAR_PALETTE = ["#fde047", "#fb923c", "#f472b6", "#c084fc", "#38bdf8"];

export const DONUT_COLORS = { success: "#198754", failed: "#c74040" };

const PASTEL_BAR_PALETTE = AGENTS_BAR_PALETTE;

const VALUE_KEY_LABELS = {
  consumed: "Tokens",
  traces: "Traces",
  sessions: "Sessions",
  executionTime: "Running time",
};

const formatChartValue = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "0";
  if (Number.isInteger(v)) return v.toLocaleString("en-US");
  return v.toLocaleString("en-US", { maximumFractionDigits: 2 });
};

const TOOLTIP_FLIP_THRESHOLD = 88;
const TOOLTIP_GAP = 12;

const ChartTooltip = ({ tooltip }) => {
  if (!tooltip) return null;
  const below = tooltip.placement === "below";
  return (
    <div
      className="dash-chart-tooltip"
      style={{
        left: tooltip.x,
        top: tooltip.y,
        transform: below
          ? `translate(-50%, ${TOOLTIP_GAP}px)`
          : `translate(-50%, calc(-100% - ${TOOLTIP_GAP}px))`,
      }}
      role="tooltip"
    >
      <div className="dash-chart-tooltip-title">{tooltip.title}</div>
      {tooltip.subtitle ? <div className="dash-chart-tooltip-sub">{tooltip.subtitle}</div> : null}
      <div className="dash-chart-tooltip-value">{tooltip.value}</div>
    </div>
  );
};

/** Viewport-fixed coords; flips below cursor when near the top of the chart. */
const positionTooltip = (wrap, event) => {
  const rect = wrap.getBoundingClientRect();
  const clientX = event?.clientX ?? 0;
  const clientY = event?.clientY ?? 0;
  const placement = clientY - rect.top < TOOLTIP_FLIP_THRESHOLD ? "below" : "above";

  return { x: clientX, y: clientY, placement };
};

const nearestPoint = (rows, xScale, pointerX) => {
  if (!rows?.length) return null;
  let nearest = rows[0];
  let minDist = Math.abs(xScale(rows[0].key) - pointerX);
  for (let i = 1; i < rows.length; i += 1) {
    const dist = Math.abs(xScale(rows[i].key) - pointerX);
    if (dist < minDist) {
      minDist = dist;
      nearest = rows[i];
    }
  }
  return nearest;
};

export const LineAreaChart = ({
  data,
  valueKey,
  color = "#3b82f6",
  soft = false,
  valueFormat = "number",
}) => {
  const wrapRef = useRef(null);
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const unitLabel = VALUE_KEY_LABELS[valueKey] || "Value";
  const isDuration = valueFormat === "duration";

  useEffect(() => {
    setTooltip(null);
    const wrap = wrapRef.current;
    const svgEl = svgRef.current;
    if (!wrap || !svgEl || !data?.length) return;

    const rows = data;
    const width = Math.max(wrap.clientWidth || 400, 280);
    const height = CHART_HEIGHT;
    const margin = isDuration ? DURATION_MARGIN : MARGIN;
    const svg = d3.select(svgEl);
    svg.attr("viewBox", `0 0 ${width} ${height}`);
    svg.selectAll("*").remove();

    const maxY = Math.max(d3.max(rows, (d) => d[valueKey]) || 0, 1);
    const x = d3
      .scalePoint()
      .domain(rows.map((d) => d.key))
      .range([margin.left, width - margin.right])
      .padding(0.4);
    const y = d3
      .scaleLinear()
      .domain([0, maxY])
      .nice()
      .range([height - margin.bottom, margin.top]);

    const formatPointValue = (raw) =>
      isDuration
        ? formatExecutionDuration(raw)
        : `${formatChartValue(raw)} ${unitLabel.toLowerCase()}`;

    const showForPoint = (event, d) => {
      const pos = positionTooltip(wrap, event);
      setTooltip({
        ...pos,
        title: d.label,
        subtitle: isDuration ? "Daily running time" : `Daily ${unitLabel.toLowerCase()}`,
        value: formatPointValue(d[valueKey]),
      });
    };

    const hideTooltip = () => setTooltip(null);

    const focus = svg.append("g").attr("class", "dash-chart-focus").style("display", "none");
    focus
      .append("line")
      .attr("class", "dash-chart-focus-line")
      .attr("y1", margin.top)
      .attr("y2", height - margin.bottom);
    const focusDot = focus
      .append("circle")
      .attr("r", soft ? 5 : 5.5)
      .attr("fill", "#fff")
      .attr("stroke", color)
      .attr("stroke-width", 2.5);

    const updateFocus = (d) => {
      if (!d) return;
      const cx = x(d.key);
      const cy = y(d[valueKey]);
      focus.style("display", null);
      focus.select(".dash-chart-focus-line").attr("x1", cx).attr("x2", cx);
      focusDot.attr("cx", cx).attr("cy", cy);
    };

    svg
      .append("g")
      .attr("transform", `translate(${margin.left},0)`)
      .call(
        d3
          .axisLeft(y)
          .ticks(4)
          .tickSizeOuter(0)
          .tickFormat((v) => (isDuration ? formatExecutionDuration(v) : v)),
      )
      .call((g) => g.selectAll("text").attr("fill", "#94a3b8").attr("font-size", isDuration ? 9 : 10))
      .call((g) => g.selectAll("path,line").attr("stroke", "rgba(15,23,42,0.06)"));

    const line = d3
      .line()
      .x((d) => x(d.key))
      .y((d) => y(d[valueKey]))
      .curve(d3.curveMonotoneX);
    const area = d3
      .area()
      .x((d) => x(d.key))
      .y0(y(0))
      .y1((d) => y(d[valueKey]))
      .curve(d3.curveMonotoneX);

    const fillOpacity = soft ? 0.32 : 0.15;
    const fillColor = d3.color(color);
    if (fillColor) fillColor.opacity = fillOpacity;

    svg.append("path").datum(rows).attr("fill", fillColor || "rgba(59,130,246,0.15)").attr("d", area);
    svg
      .append("path")
      .datum(rows)
      .attr("fill", "none")
      .attr("stroke", color)
      .attr("stroke-width", soft ? 2 : 2.5)
      .attr("stroke-linecap", "round")
      .attr("stroke-linejoin", "round")
      .attr("d", line);

    svg
      .append("rect")
      .attr("class", "dash-chart-overlay")
      .attr("x", margin.left)
      .attr("y", margin.top)
      .attr("width", width - margin.left - margin.right)
      .attr("height", height - margin.top - margin.bottom)
      .attr("fill", "transparent")
      .style("cursor", "crosshair")
      .on("mousemove", (event) => {
        const [mx] = d3.pointer(event);
        const d = nearestPoint(rows, x, mx);
        if (!d) return;
        updateFocus(d);
        showForPoint(event, d);
      })
      .on("mouseleave", () => {
        focus.style("display", "none");
        hideTooltip();
      });

    svg
      .append("g")
      .attr("class", "dash-chart-dots")
      .selectAll("circle")
      .data(rows)
      .join("circle")
      .attr("cx", (d) => x(d.key))
      .attr("cy", (d) => y(d[valueKey]))
      .attr("r", soft ? 3.5 : 4)
      .attr("fill", "#fff")
      .attr("stroke", color)
      .attr("stroke-width", soft ? 2 : 1.5)
      .style("cursor", "pointer")
      .on("mouseenter", (event, d) => {
        updateFocus(d);
        showForPoint(event, d);
      })
      .on("mousemove", (event, d) => showForPoint(event, d))
      .on("mouseleave", () => {
        focus.style("display", "none");
        hideTooltip();
      });

    const step = Math.max(1, Math.ceil(rows.length / 8));
    const ticks = rows.filter((_, i) => i % step === 0).map((d) => d.key);
    svg
      .append("g")
      .attr("transform", `translate(0,${height - margin.bottom})`)
      .call(
        d3
          .axisBottom(x)
          .tickValues(ticks)
          .tickFormat((k) => rows.find((d) => d.key === k)?.label || ""),
      )
      .call((g) => g.selectAll("text").attr("fill", "#94a3b8").attr("font-size", 9))
      .call((g) => g.selectAll("path,line").attr("stroke", "rgba(15,23,42,0.06)"));

    focus.raise();
  }, [data, valueKey, color, soft, unitLabel, isDuration]);

  if (!data?.length) {
    return <div className="dash-chart-empty">No data for this period</div>;
  }

  return (
    <div
      ref={wrapRef}
      className={`dash-chart-surface dash-chart-svg-wrap${soft ? " dash-chart-svg-wrap--soft" : ""}`}
    >
      <svg ref={svgRef} style={{ width: "100%", height: CHART_HEIGHT, display: "block" }} />
      <ChartTooltip tooltip={tooltip} />
    </div>
  );
};

export const DonutChart = ({ success, failed, colors = DONUT_COLORS }) => {
  const wrapRef = useRef(null);
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const total = (success || 0) + (failed || 0);
  const successPct = total ? Math.round((success / total) * 100) : 0;
  const failedShare = 100 - successPct;

  useEffect(() => {
    setTooltip(null);
    const wrap = wrapRef.current;
    const svgEl = svgRef.current;
    if (!wrap || !svgEl || total === 0) return;

    const size = Math.min(wrap.clientWidth || 280, 220);
    const radius = size / 2 - 8;
    const svg = d3.select(svgEl);
    svg.attr("viewBox", `0 0 ${size} ${size}`);
    svg.selectAll("*").remove();

    const g = svg.append("g").attr("transform", `translate(${size / 2},${size / 2})`);
    const pie = d3.pie().value((d) => d.value).sort(null).padAngle(0.02);
    const arc = d3.arc().innerRadius(radius * 0.58).outerRadius(radius).cornerRadius(4);
    const arcHover = d3.arc().innerRadius(radius * 0.54).outerRadius(radius + 4).cornerRadius(4);

    const segments = [
      { key: "success", value: success, label: "Success", pct: successPct },
      { key: "failed", value: failed, label: "Failed", pct: failedShare },
    ];

    g.selectAll("path")
      .data(pie(segments))
      .join("path")
      .attr("d", arc)
      .attr("fill", (d) => colors[d.data.key])
      .attr("stroke", "#fff")
      .attr("stroke-width", 2.5)
      .style("cursor", "pointer")
      .on("mouseenter", function (event, d) {
        d3.select(this).attr("d", arcHover);
        const pos = positionTooltip(wrap, event);
        setTooltip({
          ...pos,
          title: d.data.label,
          subtitle: "Request outcome",
          value: `${formatChartValue(d.data.value)} (${d.data.pct}%)`,
        });
      })
      .on("mousemove", (event, d) => {
        const pos = positionTooltip(wrap, event);
        setTooltip((prev) =>
          prev
            ? {
                ...prev,
                ...pos,
                title: d.data.label,
                subtitle: "Request outcome",
                value: `${formatChartValue(d.data.value)} (${d.data.pct}%)`,
              }
            : prev,
        );
      })
      .on("mouseleave", function () {
        d3.select(this).attr("d", arc);
        setTooltip(null);
      });

    g.append("text")
      .attr("text-anchor", "middle")
      .attr("dy", "-0.15em")
      .attr("fill", "#0f172a")
      .attr("font-size", 22)
      .attr("font-weight", 700)
      .style("pointer-events", "none")
      .text(`${successPct}%`);

    g.append("text")
      .attr("text-anchor", "middle")
      .attr("dy", "1.35em")
      .attr("fill", "#94a3b8")
      .attr("font-size", 11)
      .attr("font-weight", 500)
      .style("pointer-events", "none")
      .text("success rate");
  }, [success, failed, total, successPct, failedShare, colors]);

  if (total === 0) {
    return <div className="dash-chart-empty">No request data in range</div>;
  }

  return (
    <div ref={wrapRef} className="dash-chart-surface dash-donut-wrap">
      <svg ref={svgRef} className="dash-donut-svg" />
      <ChartTooltip tooltip={tooltip} />
      <div className="dash-donut-legend">
        <div className="dash-donut-legend-item dash-donut-legend-item--success">
          <span className="dash-donut-swatch" style={{ background: colors.success }} />
          <div className="dash-donut-legend-text">
            <span className="dash-donut-legend-label">Success</span>
            <span className="dash-donut-legend-meta">{successPct}%</span>
          </div>
          <strong className="dash-donut-legend-value">{success}</strong>
        </div>
        <div className="dash-donut-legend-item dash-donut-legend-item--failed">
          <span className="dash-donut-swatch" style={{ background: colors.failed }} />
          <div className="dash-donut-legend-text">
            <span className="dash-donut-legend-label">Failed</span>
            <span className="dash-donut-legend-meta">{failedShare}%</span>
          </div>
          <strong className="dash-donut-legend-value">{failed}</strong>
        </div>
      </div>
    </div>
  );
};

export const HorizontalBarChart = ({ items, palette = PASTEL_BAR_PALETTE }) => {
  const wrapRef = useRef(null);
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    setTooltip(null);
    const wrap = wrapRef.current;
    const svgEl = svgRef.current;
    if (!wrap || !svgEl || !items?.length) return;

    const width = Math.max(wrap.clientWidth || 400, 280);
    const height = Math.max(160, items.length * 40 + 40);
    const margin = { top: 8, right: 32, bottom: 8, left: 118 };
    const svg = d3.select(svgEl);
    svg.attr("viewBox", `0 0 ${width} ${height}`);
    svg.selectAll("*").remove();

    const maxV = Math.max(d3.max(items, (d) => d.usage) || 0, 1);
    const y = d3
      .scaleBand()
      .domain(items.map((d) => d.name))
      .range([margin.top, height - margin.bottom])
      .padding(0.32);
    const x = d3
      .scaleLinear()
      .domain([0, maxV])
      .range([margin.left, width - margin.right]);

    const trackEnd = width - margin.right;

    const showForBar = (event, d) => {
      const pos = positionTooltip(wrap, event);
      setTooltip({
        ...pos,
        title: d.name,
        subtitle: "Agent executions",
        value: `${formatChartValue(d.usage)} runs`,
      });
    };

    svg
      .selectAll("rect.track")
      .data(items)
      .join("rect")
      .attr("class", "track")
      .attr("x", margin.left)
      .attr("y", (d) => y(d.name))
      .attr("width", trackEnd - margin.left)
      .attr("height", y.bandwidth())
      .attr("rx", 8)
      .attr("fill", "#f1f5f9")
      .style("cursor", "pointer")
      .on("mouseenter", showForBar)
      .on("mousemove", showForBar)
      .on("mouseleave", () => setTooltip(null));

    svg
      .selectAll("rect.bar")
      .data(items)
      .join("rect")
      .attr("class", "bar")
      .attr("x", margin.left)
      .attr("y", (d) => y(d.name))
      .attr("width", (d) => Math.max(x(d.usage) - margin.left, 4))
      .attr("height", y.bandwidth())
      .attr("rx", 8)
      .attr("fill", (_, i) => palette[i % palette.length])
      .style("cursor", "pointer")
      .on("mouseenter", function (event, d) {
        d3.select(this).attr("opacity", 0.88);
        showForBar(event, d);
      })
      .on("mousemove", showForBar)
      .on("mouseleave", function () {
        d3.select(this).attr("opacity", 1);
        setTooltip(null);
      });

    svg
      .selectAll("text.label")
      .data(items)
      .join("text")
      .attr("class", "label")
      .attr("x", 4)
      .attr("y", (d) => (y(d.name) || 0) + y.bandwidth() / 2)
      .attr("dy", "0.35em")
      .attr("fill", "#475569")
      .attr("font-size", 11)
      .attr("font-weight", 500)
      .style("pointer-events", "none")
      .text((d) => (d.name.length > 14 ? `${d.name.slice(0, 12)}…` : d.name));

    svg
      .selectAll("text.val")
      .data(items)
      .join("text")
      .attr("class", "val")
      .attr("x", (d) => x(d.usage) + 8)
      .attr("y", (d) => (y(d.name) || 0) + y.bandwidth() / 2)
      .attr("dy", "0.35em")
      .attr("fill", "#94a3b8")
      .attr("font-size", 10)
      .attr("font-weight", 600)
      .style("pointer-events", "none")
      .text((d) => d.usage);
  }, [items, palette]);

  if (!items?.length) {
    return <div className="dash-chart-empty">No agent usage in range</div>;
  }

  return (
    <div ref={wrapRef} className="dash-chart-surface dash-chart-svg-wrap dash-chart-svg-wrap--bars">
      <svg ref={svgRef} style={{ width: "100%", minHeight: 160, display: "block" }} />
      <ChartTooltip tooltip={tooltip} />
    </div>
  );
};
